/**
 * FriendCard component
 * Simple router component that delegates to FriendCardAccepted or FriendCardPending
 * based on friendship status
 */
import React, { useMemo } from 'react'
import { View } from 'react-native'
import { useTheme } from '../../../src/theme/useTheme'
import { useThemeContext } from '../../../src/theme/ThemeContext'
import { getFriendCardStyles } from './FriendCard.styles'
import { FriendCardAccepted } from './FriendCardAccepted'
import { FriendCardPending } from './FriendCardPending'
import type { Friend } from '../../../src/friends/types/friends.types'
import type { PlayerRank } from '../../../src/economy/constants/playerRanks.constants'

interface FriendCardProps {
  friend: Friend
  cardWidth?: number
  /** Rank crest + level for accepted cards; null while unknown (specs/rank-on-social-screens AC-7). */
  playerRank?: PlayerRank | null
  /** Only passed for pending (sent) so user can cancel their friend request; accepted cards do not show remove. */
  onRemoveFriend?: (friendId: string, friend?: Friend) => void
  onAcceptInvitation?: (token: string, invitationId: string) => void
  onDeclineInvitation?: (invitationId: string) => void
  actionLoadingId?: string | null
  pendingRequestCount?: number // Total count of pending requests (for badge display)
  requestIndex?: number // Index of this request (0-based) for stacking visualization
  /** Test ID for testing */
  testID?: string
}

export const FriendCard: React.FC<FriendCardProps> = ({
  friend,
  cardWidth = 160,
  playerRank = null,
  onRemoveFriend,
  onAcceptInvitation,
  onDeclineInvitation,
  actionLoadingId,
  pendingRequestCount = 0,
  requestIndex = 0,
  testID,
}) => {
  const theme = useTheme()
  const { themeMode } = useThemeContext()
  const styles = useMemo(() => getFriendCardStyles(theme, themeMode), [theme, themeMode])
  return (
    <View
      style={
        friend.friendshipStatus === 'pending'
          ? [styles.friendCard, styles.friendCardPending]
          : styles.friendCardAcceptedOuter
      }
      testID={testID || `friend-card-${friend.id}`}
    >
      {friend.friendshipStatus === 'pending' ? (
        <FriendCardPending
          friend={friend}
          onRemoveFriend={onRemoveFriend}
          onAcceptInvitation={onAcceptInvitation}
          onDeclineInvitation={onDeclineInvitation}
          actionLoadingId={actionLoadingId}
          pendingRequestCount={pendingRequestCount}
          requestIndex={requestIndex}
        />
      ) : (
        <FriendCardAccepted
          friend={friend}
          cardWidth={cardWidth}
          playerRank={playerRank}
          onRemoveFriend={onRemoveFriend}
        />
      )}
    </View>
  )
}

